
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TeacherProfile, CreateTeacherProfileData, UpdateTeacherProfileData } from '@/types/teacherProfile';

export function useTeacherProfile(teacherCode?: string) {
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchProfile = async () => {
    if (!teacherCode) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('teacher_profiles')
        .select('*')
        .eq('teacher_code', teacherCode)
        .maybeSingle();

      if (error) {
        console.error('Error fetching teacher profile:', error);
        return;
      }

      setProfile(data);
    } catch (error) {
      console.error('Error fetching teacher profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createProfile = async (data: CreateTeacherProfileData): Promise<boolean> => {
    try {
      const { data: result, error } = await supabase
        .from('teacher_profiles')
        .insert(data)
        .select()
        .single();

      if (error) {
        toast({
          title: 'เกิดข้อผิดพลาด',
          description: 'ไม่สามารถบันทึกข้อมูลได้',
          variant: 'destructive'
        });
        return false;
      }

      setProfile(result);
      toast({
        title: 'บันทึกสำเร็จ',
        description: 'ข้อมูลส่วนตัวได้รับการบันทึกแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error creating teacher profile:', error);
      return false;
    }
  };

  const updateProfile = async (data: UpdateTeacherProfileData): Promise<boolean> => {
    if (!teacherCode) return false;

    try {
      const { data: result, error } = await supabase
        .from('teacher_profiles')
        .update(data)
        .eq('teacher_code', teacherCode)
        .select()
        .single();

      if (error) {
        toast({
          title: 'เกิดข้อผิดพลาด',
          description: 'ไม่สามารถอัพเดทข้อมูลได้',
          variant: 'destructive'
        });
        return false;
      }

      setProfile(result);
      toast({
        title: 'อัพเดทสำเร็จ',
        description: 'ข้อมูลส่วนตัวได้รับการอัพเดทแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error updating teacher profile:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [teacherCode]);

  return {
    profile,
    isLoading,
    createProfile,
    updateProfile,
    refetch: fetchProfile
  };
}
